import React, { useState } from 'react';
import { ShoppingCart, SlidersHorizontal, ChevronDown, Wrench, Grid3X3, LayoutGrid, Square } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import { Slider } from './ui/slider';

const FilterBar = ({
  sortBy,
  setSortBy,
  stockFilter,
  setStockFilter,
  priceRange,
  setPriceRange,
  totalResults,
  cartCount,
  setShowFilters,
  showFilters,
  onCartClick,
  cardSize,
  setCardSize,
}) => {
  const [localPriceMin, setLocalPriceMin] = useState(priceRange[0]);
  const [localPriceMax, setLocalPriceMax] = useState(priceRange[1]);

  const handlePriceApply = () => {
    setPriceRange([localPriceMin, localPriceMax]);
  };

  return (
    <div className="mx-4 md:mx-8 lg:mx-16 mt-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Cart Button */}
        <Button 
          onClick={onCartClick}
          className="bg-[#0a1628] hover:bg-[#0a1628]/90 text-white rounded-full px-5 py-2 flex items-center gap-2"
          data-testid="cart-btn"
        >
          <ShoppingCart className="w-4 h-4" />
          Cart
          {cartCount > 0 && (
            <span className="bg-white text-[#0a1628] rounded-full px-2 py-0.5 text-xs font-bold">
              {cartCount}
            </span>
          )}
        </Button>

        {/* TOOLS Dropdown - Contains Relevance, Stock, Price, Advanced Filters */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="rounded-lg border-[#0a1628]/20 bg-white text-[#0a1628] hover:bg-[#0a1628]/10 flex items-center gap-2"
              data-testid="tools-dropdown"
            >
              <Wrench className="w-4 h-4" />
              TOOLS
              <ChevronDown className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-64 bg-white border-[#0a1628]/20" align="start">
            <DropdownMenuLabel className="text-[#0a1628]/60 text-xs">SORTING & FILTERS</DropdownMenuLabel>
            
            {/* Sort By Sub Menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-[#0a1628]">
                <span>Sort: {sortBy === 'relevance' ? 'Relevance' : sortBy === 'price-low' ? 'Price ↑' : sortBy === 'price-high' ? 'Price ↓' : 'Name'}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-white">
                <DropdownMenuItem onClick={() => setSortBy('relevance')} className="text-[#0a1628]">
                  Relevance {sortBy === 'relevance' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('price-low')} className="text-[#0a1628]">
                  Price: Low to High {sortBy === 'price-low' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('price-high')} className="text-[#0a1628]">
                  Price: High to Low {sortBy === 'price-high' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('name')} className="text-[#0a1628]">
                  Name A-Z {sortBy === 'name' && '✓'}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Stock Filter Sub Menu */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-[#0a1628]">
                <span>Stock: {stockFilter === 'all' ? 'All' : stockFilter === 'in-stock' ? 'In Stock' : 'Out of Stock'}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-white">
                <DropdownMenuItem onClick={() => setStockFilter('all')} className="text-[#0a1628]">
                  All Stock {stockFilter === 'all' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStockFilter('in-stock')} className="text-[#0a1628]">
                  In Stock Only {stockFilter === 'in-stock' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStockFilter('out-of-stock')} className="text-[#0a1628]">
                  Out of Stock {stockFilter === 'out-of-stock' && '✓'}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            {/* Price Range */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-[#0a1628]">
                <span>Price: ${priceRange[0]} - ${priceRange[1]}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-white p-3 w-56">
                <div className="space-y-3">
                  <p className="text-xs text-[#0a1628]/60 font-medium">Price Range</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={localPriceMin}
                      onChange={(e) => setLocalPriceMin(Number(e.target.value))}
                      className="w-20 text-sm"
                      min="0"
                      placeholder="Min"
                    />
                    <span className="text-[#0a1628]/40">-</span>
                    <Input
                      type="number"
                      value={localPriceMax}
                      onChange={(e) => setLocalPriceMax(Number(e.target.value))}
                      className="w-20 text-sm"
                      min="0"
                      placeholder="Max"
                    />
                  </div>
                  <Button 
                    size="sm" 
                    onClick={handlePriceApply}
                    className="w-full bg-[#0a1628] text-white"
                  >
                    Apply
                  </Button>
                </div>
              </DropdownMenuSubContent>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            {/* Advanced Filters */}
            <DropdownMenuItem 
              onClick={() => setShowFilters(!showFilters)}
              className="text-[#0a1628]"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Advanced Filters {showFilters && '✓'}
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            
            <DropdownMenuLabel className="text-[#0a1628]/60 text-xs">DISPLAY</DropdownMenuLabel>
            
            {/* Card Size Options */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="text-[#0a1628]">
                <LayoutGrid className="w-4 h-4 mr-2" />
                <span>Card Size: {cardSize === 'small' ? 'Small' : cardSize === 'medium' ? 'Medium' : 'Large'}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="bg-white">
                <DropdownMenuItem onClick={() => setCardSize && setCardSize('small')} className="text-[#0a1628]">
                  <Grid3X3 className="w-4 h-4 mr-2" />
                  Small {cardSize === 'small' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCardSize && setCardSize('medium')} className="text-[#0a1628]">
                  <LayoutGrid className="w-4 h-4 mr-2" />
                  Medium {cardSize === 'medium' && '✓'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCardSize && setCardSize('large')} className="text-[#0a1628]">
                  <Square className="w-4 h-4 mr-2" />
                  Large {cardSize === 'large' && '✓'}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Results Count */}
        <span className="text-[#0a1628] font-semibold">{totalResults} results</span>
      </div>
    </div>
  );
};

export default FilterBar;
